/**
 * Malfreund commerce. Loaded with `defer` right after mf-theme.js, no dependencies.
 * Only global: window.mfTheme.commerce
 *
 * Custom elements
 *   <mf-product-form>  buy box (snippets/mf-buy-box.liquid; also used inside the sticky bar)
 *   <mf-quantity>      − / + stepper around a number input
 *   <mf-cart-drawer>   cart drawer #MfCartDrawer (extends mfTheme.Drawer)
 *   <mf-gallery>       product gallery (scroll-snap slider + dots + thumbnails)
 *   <mf-sticky-buy>    sticky buy bar
 *
 * Declarative hooks (usable by any section)
 *   [data-mf-buy-button]        primary buy button: the sticky bar hides while one of them is visible
 *   [data-mf-add-to-cart]       click adds the currently selected variant ([data-product-id],
 *                               [data-variant-id], [data-quantity] optional); falls back to the
 *                               element's own href when nothing can be added
 *   [data-mf-variant-media]     value = variant id or option value(s), comma separated. Siblings in
 *                               the same group ([data-mf-variant-media-group], default: the parent)
 *                               are shown / hidden (`hidden` + class is-active) with the selection
 *   [data-mf-cart-toggle]       (foundation) opens the cart drawer
 *
 * Cart markup contract (sections/mf-cart-drawer.liquid, mf-main-cart.liquid)
 *   [data-mf-cart-section="<section id>"]  root of a section that shows cart contents
 *     [data-mf-cart-part="<name>"]         regions replaced after every cart change
 *     [data-mf-cart-live]                  polite live region      [data-mf-cart-error]  error box
 *     [data-mf-line][data-key]             line item → [data-mf-line-quantity], [data-mf-line-remove]
 *
 * Events: emits mf:variant:change { productId, variant, options, sectionId }
 * Colour:  --mf-variant-color on <html> = colour of the selected swatch (input[data-mf-swatch-color]),
 *          set at init and on every variant change; removed when unknown (CSS falls back to green)
 *         emits mf:cart:updated { cart } and mf:cart:open { opener }
 *
 * API: mfTheme.commerce.getVariant(productId) · .add({ id, quantity, properties }, opener)
 *      .refresh() (re-render cart sections + count)
 */
(() => {
  'use strict';

  const mf = window.mfTheme;
  if (!mf || !mf.Drawer || !mf.routes) return;

  const doc = document;
  const root = doc.documentElement;
  const strings = mf.strings || {};
  const norm = (value) => String(value).trim().toLowerCase();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const plainClick = (event) =>
    !(event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

  /* Cart requests ------------------------------------------------------------ */
  const sectionIds = () => [...new Set([...doc.querySelectorAll('[data-mf-cart-section]')].map((el) => el.dataset.mfCartSection))];

  const post = async (url, body) => {
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    const isForm = body instanceof FormData;
    if (!isForm) headers['Content-Type'] = 'application/json';
    const response = await fetch(url, { method: 'POST', credentials: 'same-origin', headers, body: isForm ? body : JSON.stringify(body) });
    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      /* not JSON: handled below */
    }
    if (!response.ok || !data) {
      // Shopify explains 422 errors (stock limits …) in `description`
      let message = data && (data.description || data.message);
      if (message && typeof message === 'object') message = Object.values(message).flat().join(' ');
      throw new Error(message || strings.cartError);
    }
    return data;
  };

  const getJson = (url) =>
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(strings.cartError);
      return response.json();
    });

  /* Cart rendering ----------------------------------------------------------- */
  const cartSection = (id) => [...doc.querySelectorAll('[data-mf-cart-section]')].find((el) => el.dataset.mfCartSection === id);

  // Remember which control of which line has focus, to put it back after the HTML was replaced
  const focusMemo = () => {
    const active = doc.activeElement;
    const section = active && active.closest && active.closest('[data-mf-cart-section]');
    if (!section || !active.closest('[data-mf-cart-part]')) return null;
    const line = active.closest('[data-mf-line]');
    return { section, key: line && line.dataset.key, role: active.getAttribute('data-mf-focus') };
  };

  const restoreFocus = (memo) => {
    if (!memo || memo.section.contains(doc.activeElement)) return;
    const line = memo.key && [...memo.section.querySelectorAll('[data-mf-line]')].find((el) => el.dataset.key === memo.key);
    const candidates = [
      line && memo.role && line.querySelector(`[data-mf-focus="${memo.role}"]`),
      line && line.querySelector('[data-mf-line-quantity]'),
      memo.section.querySelector('[data-mf-cart-focus]'),
      memo.section.closest('.mf-drawer__panel'),
    ];
    const target = candidates.find(Boolean);
    if (target) target.focus({ preventScroll: true });
  };

  const applyParts = (live, fresh) => {
    live.querySelectorAll('[data-mf-cart-part]').forEach((part) => {
      const next = [...fresh.querySelectorAll('[data-mf-cart-part]')].find((el) => el.dataset.mfCartPart === part.dataset.mfCartPart);
      if (next) part.innerHTML = next.innerHTML;
    });
  };

  const renderSections = async (sections) => {
    if (!sections) return;
    const memo = focusMemo();
    const missing = [];
    Object.keys(sections).forEach((id) => {
      const live = cartSection(id);
      if (!live) return;
      if (sections[id]) applyParts(live, new DOMParser().parseFromString(sections[id], 'text/html'));
      else missing.push(live);
    });
    if (missing.length) {
      // Shopify could not render this section on its own: take its parts from the page itself
      try {
        const html = await fetch(window.location.href, { credentials: 'same-origin' }).then((response) => response.text());
        const page = new DOMParser().parseFromString(html, 'text/html');
        missing.forEach((live) => {
          const fresh = [...page.querySelectorAll('[data-mf-cart-section]')].find((el) => el.dataset.mfCartSection === live.dataset.mfCartSection);
          if (fresh) applyParts(live, fresh);
        });
      } catch (error) {
        /* keep the current markup */
      }
    }
    restoreFocus(memo);
  };

  const announce = (text) => {
    doc.querySelectorAll('[data-mf-cart-live]').forEach((el) => {
      el.textContent = '';
      window.setTimeout(() => {
        el.textContent = text;
      }, 150);
    });
  };

  const showCartError = (message) => {
    doc.querySelectorAll('[data-mf-cart-error]').forEach((el) => {
      el.textContent = message || '';
      el.hidden = !message;
    });
  };

  const setCartBusy = (busy) => {
    doc.querySelectorAll('[data-mf-cart-section]').forEach((el) => {
      el.classList.toggle('is-loading', busy);
      el.setAttribute('aria-busy', String(busy));
    });
  };

  const countText = (cart) => (strings.cartCount || '').replace('[count]', cart.item_count);

  const cartUpdated = (cart, message) => {
    mf.emit('mf:cart:updated', { cart });
    announce([message, countText(cart)].filter(Boolean).join('. '));
  };

  const refresh = async () => {
    const ids = sectionIds();
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    try {
      const [sections, cart] = await Promise.all([ids.length ? getJson(url) : null, getJson(mf.routes.cartJs)]);
      await renderSections(sections);
      mf.emit('mf:cart:updated', { cart });
      return cart;
    } catch (error) {
      return null;
    }
  };

  /* Add to cart ---------------------------------------------------------------- */
  let adding = false;

  const add = async (item, opener) => {
    if (adding) return false;
    adding = true;
    try {
      const ids = sectionIds();
      let body = item;
      if (body instanceof FormData) {
        if (ids.length) {
          body.set('sections', ids.join(','));
          body.set('sections_url', window.location.pathname);
        }
      } else {
        body = { items: [{ id: item.id, quantity: item.quantity || 1, properties: item.properties }], sections: ids, sections_url: window.location.pathname };
      }
      const data = await post(mf.routes.cartAdd, body);
      showCartError('');
      const drawer = doc.getElementById('MfCartDrawer');
      if (!drawer || typeof drawer.open !== 'function') {
        window.location.assign(mf.routes.cart);
        return true;
      }
      await renderSections(data.sections);
      mf.emit('mf:cart:open', { opener });
      getJson(mf.routes.cartJs)
        .then((cart) => cartUpdated(cart, strings.cartAdded))
        .catch(() => {});
      return true;
    } finally {
      adding = false;
    }
  };

  /* Line items: quantity and remove -------------------------------------------- */
  let queue = Promise.resolve();
  const timers = new Map();

  const changeLine = (key, quantity, message) => {
    queue = queue.then(async () => {
      setCartBusy(true);
      try {
        const data = await post(mf.routes.cartChange, { id: key, quantity, sections: sectionIds(), sections_url: window.location.pathname });
        const sections = data.sections;
        delete data.sections;
        await renderSections(sections);
        const item = (data.items || []).find((line) => line.key === key);
        // Older stores answer 200 and silently cap the quantity at the stock level
        const capped = quantity > 0 && item && item.quantity < quantity;
        showCartError(capped ? (strings.cartQuantityError || '').replace('[quantity]', item.quantity) : '');
        cartUpdated(data, message || strings.cartUpdated);
      } catch (error) {
        await refresh(); // put the real quantities back
        showCartError(error.message);
      } finally {
        setCartBusy(false);
      }
    });
    return queue;
  };

  doc.addEventListener('change', (event) => {
    const input = event.target instanceof Element && event.target.closest('[data-mf-line-quantity]');
    const line = input && input.closest('[data-mf-line]');
    if (!line) return;
    const key = line.dataset.key;
    let quantity = parseInt(input.value, 10);
    if (Number.isNaN(quantity) || quantity < 0) {
      input.value = input.defaultValue;
      return;
    }
    const max = parseInt(input.max, 10);
    if (max && quantity > max) quantity = max;
    window.clearTimeout(timers.get(key));
    timers.set(
      key,
      window.setTimeout(() => {
        timers.delete(key);
        changeLine(key, quantity);
      }, 350)
    );
  });

  doc.addEventListener('click', (event) => {
    if (!plainClick(event) || !(event.target instanceof Element)) return;

    const remove = event.target.closest('[data-mf-line-remove]');
    if (remove) {
      const line = remove.closest('[data-mf-line]');
      if (!line) return;
      event.preventDefault();
      window.clearTimeout(timers.get(line.dataset.key));
      changeLine(line.dataset.key, 0, remove.dataset.message);
      return;
    }

    // Any link / button can add the currently selected variant
    const trigger = event.target.closest('[data-mf-add-to-cart]');
    if (!trigger || trigger.getAttribute('aria-busy') === 'true') return;
    const productId = trigger.dataset.productId || current.keys().next().value;
    const variant = trigger.dataset.variantId ? { id: trigger.dataset.variantId, available: true } : current.get(String(productId));
    if (!variant || !variant.available) return; // let the link do its job (e.g. jump to the product area)
    event.preventDefault();
    trigger.setAttribute('aria-busy', 'true');
    add({ id: variant.id, quantity: parseInt(trigger.dataset.quantity, 10) || 1 }, trigger)
      .catch((error) => {
        showCartError(error.message);
        mf.emit('mf:cart:open', { opener: trigger });
      })
      .finally(() => trigger.removeAttribute('aria-busy'));
  });

  // Enter inside a quantity field must not submit the cart form (that would go to the checkout)
  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof Element) || !event.target.matches('[data-mf-line-quantity]')) return;
    event.preventDefault();
    event.target.dispatchEvent(new Event('change', { bubbles: true }));
  });

  doc.addEventListener('submit', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-mf-cart-form]')) return;
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') {
      event.preventDefault();
      return;
    }
    submitter.setAttribute('aria-busy', 'true');
  });

  // Back from the checkout (page restored from the back/forward cache): cart and buttons may be stale
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });

  /* Quantity stepper ------------------------------------------------------------ */
  class Quantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      if (!this.bound) {
        this.bound = true;
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-mf-quantity-step]');
          if (!button || button.getAttribute('aria-disabled') === 'true') return;
          const step = Number(button.dataset.mfQuantityStep) * (parseInt(this.input.step, 10) || 1);
          this.input.value = this.clamp((parseInt(this.input.value, 10) || 0) + step);
          this.sync();
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this.addEventListener('change', (event) => {
          if (event.target !== this.input || !event.isTrusted) return;
          const value = parseInt(this.input.value, 10);
          this.input.value = Number.isNaN(value) ? this.input.defaultValue : this.clamp(value);
          this.sync();
        });
      }
      this.sync();
    }

    clamp(value) {
      const min = parseInt(this.input.min, 10) || 0;
      const max = parseInt(this.input.max, 10) || Infinity;
      return Math.min(Math.max(value, min), max);
    }

    // aria-disabled instead of disabled: the button keeps the focus when it reaches the limit
    sync() {
      const value = parseInt(this.input.value, 10) || 0;
      this.querySelectorAll('[data-mf-quantity-step]').forEach((button) => {
        const atLimit = Number(button.dataset.mfQuantityStep) < 0 ? value <= this.clamp(-Infinity) : value >= this.clamp(Infinity);
        button.setAttribute('aria-disabled', String(atLimit));
      });
    }
  }
  customElements.define('mf-quantity', Quantity);

  /* Selected variant per product, shared by every form on the page --------------- */
  const current = new Map(); // product id (string) → variant | null
  let emitter = null;

  const swapMedia = (productId, variant) => {
    if (!variant) return;
    const keys = [String(variant.id), ...variant.options.map(norm)];
    const groups = new Map();
    doc.querySelectorAll('[data-mf-variant-media]').forEach((el) => {
      const group = el.closest('[data-mf-variant-media-group]') || el.parentElement;
      const scope = el.closest('[data-product-id]');
      if (scope && scope.dataset.productId !== String(productId)) return;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(el);
    });
    groups.forEach((items) => {
      const matches = items.filter((el) => el.dataset.mfVariantMedia.split(',').some((key) => keys.includes(norm(key))));
      if (!matches.length) return; // nothing for this variant: keep what is shown
      items.forEach((el) => {
        const on = matches.includes(el);
        el.hidden = !on;
        el.classList.toggle('is-active', on);
      });
    });
  };

  // Selected colour → --mf-variant-color (tinted closing band, rings). Unknown colour: CSS fallback.
  const paintVariant = (variant) => {
    if (!variant) return;
    const keys = variant.options.map(norm);
    const input = [...doc.querySelectorAll('input[data-mf-swatch-color]')].find((el) => keys.includes(norm(el.dataset.value)));
    if (input) root.style.setProperty('--mf-variant-color', input.dataset.mfSwatchColor);
    else root.style.removeProperty('--mf-variant-color');
  };

  const setCurrent = (form, variant, options) => {
    current.set(form.productId, variant);
    swapMedia(form.productId, variant);
    paintVariant(variant);
    emitter = form;
    mf.emit('mf:variant:change', { productId: form.productId, variant, options, sectionId: form.dataset.sectionId });
    emitter = null;
  };

  /* Product form ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      const form = this.querySelector('form');
      const json = this.querySelector('script[data-mf-product-json]');
      if (!form || !json) return;
      let data;
      try {
        data = JSON.parse(json.textContent);
      } catch (error) {
        return; // stays a plain form
      }
      this.form = form;
      this.variants = data.variants || [];
      this.str = data.strings || {};
      this.productId = String(this.dataset.productId);
      this.inputs = [...this.querySelectorAll('input[data-mf-option]')];
      this.idInput = this.querySelector('[data-mf-variant-id]');
      this.button = this.querySelector('[data-mf-submit]');
      this.errorEl = this.querySelector('[data-mf-error]');

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-mf-option]')) this.onPick();
      });
      form.addEventListener('submit', (event) => this.onSubmit(event));
      this.off = mf.on('mf:variant:change', (event) => this.onSync(event.detail));

      // Radios can come back from the browser's form restore: trust them over the server state
      this.variant = this.inputs.length ? this.resolve() : this.byId(this.dataset.variantId);
      if (current.has(this.productId)) {
        const shared = current.get(this.productId);
        if (shared && (!this.variant || shared.id !== this.variant.id)) this.adopt(shared.options, shared);
      } else {
        current.set(this.productId, this.variant);
        swapMedia(this.productId, this.variant);
        paintVariant(this.variant);
      }
      this.render();
    }

    disconnectedCallback() {
      if (this.off) this.off();
      this.off = null;
      this.form = null;
    }

    byId(id) {
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    selected() {
      const values = [];
      this.inputs.forEach((input) => {
        if (input.checked) values[Number(input.dataset.position) - 1] = input.dataset.value;
      });
      return values;
    }

    find(values) {
      return this.variants.find((variant) => variant.options.every((option, index) => option === values[index])) || null;
    }

    resolve() {
      return this.find(this.selected());
    }

    onPick() {
      this.variant = this.resolve();
      this.showError('');
      this.render();
      if (this.variant && this.hasAttribute('data-mf-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variant.id);
        window.history.replaceState(window.history.state, '', url);
      }
      setCurrent(this, this.variant, this.selected());
    }

    // Another form of the same product changed its selection
    onSync(detail) {
      if (emitter === this || String(detail.productId) !== this.productId) return;
      this.adopt(detail.options, detail.variant);
      this.showError('');
      this.render();
    }

    adopt(options, variant) {
      if (this.inputs.length && options) {
        this.inputs.forEach((input) => {
          input.checked = options[Number(input.dataset.position) - 1] === input.dataset.value;
        });
        this.variant = this.resolve();
      } else {
        this.variant = variant ? this.byId(variant.id) : null;
      }
    }

    render() {
      const variant = this.variant;
      const values = this.inputs.length ? this.selected() : variant ? variant.options : [];

      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !variant;
      }
      this.querySelectorAll('[data-mf-selected-value]').forEach((el) => {
        const value = values[Number(el.dataset.mfSelectedValue) - 1];
        if (value != null) el.textContent = value;
      });
      this.querySelectorAll('[data-mf-variant-title]').forEach((el) => {
        if (variant) el.textContent = variant.title;
      });
      if (variant) this.renderPrice(variant);

      if (this.button) {
        const label = this.button.querySelector('[data-mf-submit-label]') || this.button;
        const text = !variant ? strings.unavailable : variant.available ? label.dataset.label || strings.addToCart : strings.soldOut;
        this.button.disabled = !variant || !variant.available;
        if (text && label.textContent.trim() !== text) label.textContent = text;
      }

      // Mark every choice that is sold out / does not exist together with the other selected values
      this.inputs.forEach((input) => {
        const probe = values.slice();
        probe[Number(input.dataset.position) - 1] = input.dataset.value;
        const match = this.find(probe);
        const state = !match ? 'unavailable' : match.available ? '' : 'sold-out';
        const choice = input.closest('[data-mf-choice]');
        const fieldset = input.closest('[data-option-name]');
        const template = !match ? this.str.unavailable : match.available ? this.str.select : this.str.soldOut;
        if (template) {
          input.setAttribute('aria-label', template.replace('[name]', fieldset ? fieldset.dataset.optionName : '').replace('[value]', input.dataset.value));
        }
        if (!choice) return;
        if (state) choice.dataset.state = state;
        else delete choice.dataset.state;
        const note = choice.querySelector('[data-mf-choice-note]');
        if (note) {
          note.textContent = !match ? this.str.unavailableShort || '' : match.available ? '' : this.str.soldOutShort || '';
          note.hidden = !state;
        }
      });
    }

    renderPrice(variant) {
      const sale = variant.compare_at_price > variant.price;
      this.querySelectorAll('[data-mf-price]').forEach((el) => {
        const now = el.querySelector('[data-mf-price-current]');
        if (now) now.innerHTML = mf.formatMoney(variant.price);
        const compare = el.querySelector('[data-mf-price-compare]');
        if (compare) {
          compare.hidden = !sale;
          const struck = compare.querySelector('s');
          if (struck) struck.innerHTML = sale ? mf.formatMoney(variant.compare_at_price) : '';
        }
        const label = el.querySelector('[data-mf-price-label]');
        if (label && label.dataset.sale) label.textContent = sale ? label.dataset.sale : label.dataset.regular;
        el.classList.toggle('mf-price--sale', sale);
        const unit = el.querySelector('[data-mf-price-unit]');
        if (unit) {
          unit.hidden = !variant.unit_price;
          const value = unit.querySelector('[data-mf-price-unit-value]');
          if (value) value.innerHTML = variant.unit_price ? `${mf.formatMoney(variant.unit_price)} /&nbsp;${variant.unit}` : '';
        }
      });
    }

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      } else if (message) {
        // No room for a message (sticky bar): show it in the cart drawer
        showCartError(message);
        mf.emit('mf:cart:open', { opener: this.button });
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy || !this.variant || !this.variant.available) return;
      this.busy = true;
      this.showError('');
      if (this.button) this.button.setAttribute('aria-busy', 'true');
      try {
        const body = new FormData(this.form);
        body.set('id', this.variant.id);
        await add(body, this.button);
      } catch (error) {
        this.showError(error.message || strings.cartError);
      } finally {
        this.busy = false;
        if (this.button) this.button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('mf-product-form', ProductForm);

  /* Cart drawer --------------------------------------------------------------------- */
  class CartDrawer extends mf.Drawer {
    close(restoreFocus) {
      super.close(restoreFocus);
      showCartError('');
    }
  }
  customElements.define('mf-cart-drawer', CartDrawer);

  if (mf.designMode) {
    // Theme editor: show the drawer while its section is selected
    const drawerOf = (event) => event.target.querySelector && event.target.querySelector('mf-cart-drawer');
    doc.addEventListener('shopify:section:select', (event) => {
      const drawer = drawerOf(event);
      if (drawer) drawer.open();
    });
    doc.addEventListener('shopify:section:deselect', (event) => {
      const drawer = drawerOf(event);
      if (drawer) drawer.close(false);
    });
    // … and open the accordion row of the selected block (product area)
    doc.addEventListener('shopify:block:select', (event) => {
      if (event.target instanceof HTMLDetailsElement) event.target.open = true;
    });
  }

  /* Product gallery ------------------------------------------------------------------- */
  class Gallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mf-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-mf-slide]')];
      if (!this.track || !this.slides.length) return;
      this.thumbs = [...this.querySelectorAll('[data-mf-thumb]')];
      this.steps = [...this.querySelectorAll('[data-mf-gallery-step]')];
      this.dots = [...this.querySelectorAll('[data-mf-gallery-dot]')];
      this.counter = this.querySelector('[data-mf-gallery-current]');
      this.index = 0;

      if (!this.bound) {
        this.bound = true;
        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-mf-thumb]');
          if (thumb) this.go(this.thumbs.indexOf(thumb));
          const step = event.target.closest('[data-mf-gallery-step]');
          if (step) this.go(this.index + Number(step.dataset.mfGalleryStep));
        });
        this.track.addEventListener('keydown', (event) => {
          const target = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
          if (target === undefined) return;
          event.preventDefault();
          this.go(target);
        });
      }

      // The slide nearest to the scroll position is the active one
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          const width = this.track.clientWidth;
          if (width) this.setActive(Math.min(Math.round(this.track.scrollLeft / width), this.slides.length - 1));
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.off = mf.on('mf:variant:change', (event) => this.onVariant(event.detail));
      this.setActive(0);
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.off) this.off();
    }

    go(index, instant) {
      const slide = this.slides[Math.min(Math.max(index, 0), this.slides.length - 1)];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
    }

    setActive(index) {
      if (index < 0 || (index === this.index && this.ready)) return;
      this.ready = true;
      this.index = index;
      if (this.counter) this.counter.textContent = index + 1;
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      this.steps.forEach((step) => {
        step.disabled = Number(step.dataset.mfGalleryStep) < 0 ? index === 0 : index === this.slides.length - 1;
      });
      // Keep the active thumbnail inside its own scroll row / column (never scrolls the page)
      const thumb = this.thumbs[index];
      const row = thumb && thumb.closest('[data-mf-thumbs]');
      if (row && (row.scrollWidth > row.clientWidth || row.scrollHeight > row.clientHeight)) {
        row.scrollTo({
          left: thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2,
          top: thumb.offsetTop - (row.clientHeight - thumb.offsetHeight) / 2,
          behavior: reducedMotion() ? 'auto' : 'smooth',
        });
      }
    }

    onVariant(detail) {
      const variant = detail.variant;
      if (!variant || String(detail.productId) !== String(this.dataset.productId)) return;
      const options = variant.options.map(norm);
      const index = this.slides.findIndex(
        (slide) =>
          (variant.media_id && slide.dataset.mediaId === String(variant.media_id)) ||
          (slide.dataset.variantValue && options.includes(norm(slide.dataset.variantValue)))
      );
      if (index < 0 || index === this.index) return;
      const box = this.getBoundingClientRect();
      const onScreen = box.bottom > 0 && box.top < window.innerHeight;
      this.go(index, !onScreen);
    }
  }
  customElements.define('mf-gallery', Gallery);

  /* Sticky buy bar ------------------------------------------------------------------- */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.seen = new Set();
      this.buttons = [];
      this.inert = true;
      this.update = this.update.bind(this);
      this.scan = this.scan.bind(this);
      const header = doc.querySelector('.mf-header-section');
      this.offset = header ? header.offsetHeight : 0;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.seen.add(entry.target);
            else this.seen.delete(entry.target);
          });
          this.update();
        },
        { rootMargin: `-${this.offset}px 0px 0px 0px` }
      );
      this.offs = [mf.on('mf:drawer:open', this.update), mf.on('mf:drawer:close', this.update), mf.on('shopify:section:load', this.scan)];
      window.addEventListener('resize', this.update);
      this.scan();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      (this.offs || []).forEach((off) => off());
      window.removeEventListener('resize', this.update);
      root.style.setProperty('--mf-sticky-bar-h', '0px');
    }

    scan() {
      this.observer.disconnect();
      this.seen.clear();
      this.buttons = [...doc.querySelectorAll('[data-mf-buy-button]')].filter((el) => !this.contains(el));
      this.buttons.forEach((el) => this.observer.observe(el));
      this.update();
    }

    update() {
      const first = this.buttons.find((el) => el.getClientRects().length);
      const hasForm = [...doc.querySelectorAll('mf-product-form')].some(
        (form) => !this.contains(form) && form.dataset.productId === this.dataset.productId
      );
      const show = Boolean(first && hasForm && !mf.Drawer.current && !this.seen.size && first.getBoundingClientRect().bottom <= this.offset);
      if (show !== this.classList.contains('is-visible')) {
        this.classList.toggle('is-visible', show);
        this.inert = !show;
      }
      // The bar floats above the bottom edge: reserve its height plus that distance
      const gap = show ? parseFloat(window.getComputedStyle(this).bottom) || 0 : 0;
      root.style.setProperty('--mf-sticky-bar-h', show ? `${Math.ceil(this.offsetHeight + gap)}px` : '0px');
    }
  }
  customElements.define('mf-sticky-buy', StickyBuy);

  mf.commerce = {
    getVariant: (productId) => current.get(String(productId)) || null,
    add: (item, opener) => add(item, opener),
    refresh,
  };
})();
